import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface DialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Wide layout for show-once credential lists. */
  size?: 'md' | 'lg';
  /** Suppress the close affordances when dismissing would lose data. */
  dismissible?: boolean;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={dismissible ? onOpenChange : undefined}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-ink/20 backdrop-blur-[2px]" />
        <RadixDialog.Content
          onEscapeKeyDown={(event) => !dismissible && event.preventDefault()}
          onInteractOutside={(event) => !dismissible && event.preventDefault()}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            'card max-h-[calc(100vh-4rem)] overflow-y-auto shadow-lg',
            size === 'lg' ? 'max-w-3xl' : 'max-w-md'
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
            <div>
              <RadixDialog.Title className="text-sm font-semibold text-ink">{title}</RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="mt-0.5 text-[13px] text-ink-secondary">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            {dismissible && (
              <RadixDialog.Close
                className="rounded p-1 text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
                aria-label="Close"
              >
                <X className="size-4" />
              </RadixDialog.Close>
            )}
          </header>

          <div className="px-4 py-4">{children}</div>

          {footer && (
            <footer className="flex justify-end gap-2 border-t border-line px-4 py-3">{footer}</footer>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
